<script setup lang="ts">
import type { ComponentExposed } from "vue-component-type-helpers"
import type { Step } from "@/components/ui/Stepper/Stepper.vue"
import type { EventVault } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { useHead } from "@unhead/vue"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, onMounted, ref, useTemplateRef, watch, watchEffect } from "vue"
import { useRouter } from "vue-router"
import { GET_USER_KEY, useGetPaymentInfo, useUpgradeFreemiumEvent } from "@/api"
import { EventDetailsForm, EventSettingsForm, FreemiumUpgradePromptModal, ProfileCreateForm } from "@/components"
import { AppLink, Card, Stepper } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { isErrorCode, NotFoundException, UnauthorisedException, ValidationException } from "@/http"
import { HostLayout } from "@/layouts"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { FreemiumUpgradePromptModalKey } from "@/symbols"

definePage({
  meta: {
    requiresAuth: false,
  },
})

const eventVaultStore = useEventVaultStore()
const urlParams = useUrlSearchParams<{
  token?: string
  paymentToken?: string
  email?: string
  eventVaultId?: string
  packageSubscriptionId?: string
  error?: unknown
}>()

const queryClient = useQueryClient()
const toast = useToast()

const paymentToken = computed(
  () => urlParams.paymentToken ?? urlParams.token ?? "",
)
const hasPaymentToken = computed(() => Boolean(paymentToken.value))

const {
  data: paymentInfo,
  isLoading: isPaymentLoading,
  isError: isPaymentError,
  error: paymentError,
} = useGetPaymentInfo(
  paymentToken.value,
  urlParams?.email,
  urlParams?.packageSubscriptionId,
)

const { t } = useTranslation()

useHead({
  title: t("onboarding.page_title"),
})

const router = useRouter()

const upgradeableFreemiumEvent = computed(() => paymentInfo.value?.upgradeableFreemiumEvent)
const hasResolvedFreemiumUpgradePrompt = ref(false)

const { toggle: toggleFreemiumUpgradePromptModal, isActive: isFreemiumUpgradePromptModalActive } = useModal(FreemiumUpgradePromptModalKey)
const { mutate: mutateUpgradeFreemiumEvent, isPending: isUpgradingFreemiumEvent } = useUpgradeFreemiumEvent()

watch(upgradeableFreemiumEvent, (event) => {
  if (event && !hasResolvedFreemiumUpgradePrompt.value && !isFreemiumUpgradePromptModalActive.value) {
    toggleFreemiumUpgradePromptModal()
  }
}, { immediate: true })

function onCreateNewEvent() {
  hasResolvedFreemiumUpgradePrompt.value = true
}

function onUpgradeFreemiumEvent() {
  const event = upgradeableFreemiumEvent.value

  if (!event)
    return

  mutateUpgradeFreemiumEvent(
    { token: paymentToken.value, eventVaultId: event.eventVaultId },
    {
      onSuccess: (upgradedEventVaultId) => {
        hasResolvedFreemiumUpgradePrompt.value = true
        toggleFreemiumUpgradePromptModal()

        router.push({
          path: `/event/${upgradedEventVaultId}/`,
          query: { message: t("onboarding.upgrade_prompt.success_message") },
        })
      },
      onError: () => {
        toast.error({ description: t("onboarding.upgrade_prompt.error_message") })
      },
    },
  )
}

const steps = ref<Step[]>([
  {
    key: "step-1",
    label: t("onboarding.profile.title"),
    state: "INCOMPLETE",
  },
  {
    key: "step-2",
    label: t("onboarding.configure.title"),
    state: "INCOMPLETE",
    disabled: true,
  },
])

function setStepField<K extends keyof Step>(
  key: string | symbol,
  field: K,
  value: Step[K],
) {
  const step = steps.value.find(s => s.key === key)

  if (!step)
    return

  step[field] = value
}

const stepperRef = useTemplateRef<ComponentExposed<typeof Stepper>>("stepper")

function completeStep(key: string | symbol) {
  setStepField(key, "state", "COMPLETE")
  stepperRef.value?.next()
}

function onUserLoaded(key: string | symbol) {
  setStepField(key, "disabled", true)
  setStepField("step-2", "disabled", false)
  completeStep(key)
}

async function onProfileSuccess(key: string | symbol) {
  await queryClient.invalidateQueries({
    queryKey: [GET_USER_KEY],
  })

  toast.success({
    title: t("onboarding.profile.success_title"),
    description: t("onboarding.profile.success_content"),
    icon: "CircleCheck",
  })

  setStepField("step-2", "disabled", false)
  completeStep(key)
}

function onProfileError() {
  toast.error()
}

function onEventDetailsSuccess(eventVault: EventVault, step: string | symbol) {
  toast.success({
    title: t("onboarding.configure.success_title"),
    description: t("onboarding.configure.success_content"),
    icon: "CircleCheck",
  })

  if (eventVault.id) {
    eventVaultStore.setEventVaultId(eventVault.id)
    urlParams.eventVaultId = eventVault.id.toString()
  }

  if (hasPaymentToken.value) {
    setStepField("step-3", "disabled", false)
    completeStep(step)
  }
  else {
    finishOnboarding(step)
  }
}

function onEventDetailsError(error?: unknown) {
  if (error instanceof ValidationException) {
    toast.error()
  }
}

function onEventSettingsSuccess(step: string | symbol) {
  finishOnboarding(step)
}

function onEventSettingsError() {
  toast.error()
}

function finishOnboarding(step: string | symbol) {
  completeStep(step)

  router.push(`/event/${eventVaultStore.eventVaultId}/`)
}

onMounted(() => {
  if (hasPaymentToken.value) {
    steps.value.push({
      key: "step-3",
      label: t("onboarding.settings.title"),
      state: "INCOMPLETE",
      disabled: !eventVaultStore.eventVaultId,
    })
  }

  const errorParam = urlParams.error
  if (isErrorCode(errorParam)) {
    const errorKey = errorParam

    toast.error({
      title: t("something_went_wrong"),
      description: t(`error_codes.${errorKey}`),
    })
  }
})

watchEffect(() => {
  if (!isPaymentError.value)
    return

  if (paymentError.value instanceof NotFoundException) {
    return router.push({ path: "/403" })
  }

  if (paymentError.value instanceof UnauthorisedException) {
    return router.push({
      path: "/login",
      query: { redirect: router.currentRoute.value.fullPath },
    })
  }

  router.push({
    path: "/event/dashboard",
    query: {
      message:
        paymentError.value?.message || t("onboarding.profile.access_denied"),
    },
  })
})
</script>

<template>
  <HostLayout>
    <Stepper ref="stepper" :steps="steps">
      <template #step-1="{ step }">
        <Card v-if="isPaymentLoading">
          <div class="space-y-4">
            <div class="gpx-skeleton h-10" />
            <div class="gpx-skeleton h-10" />
            <div class="gpx-skeleton h-10" />
            <div class="gpx-skeleton h-32" />
          </div>
        </Card>
        <ProfileCreateForm
          @user-loaded="() => onUserLoaded(step.key)"
          @success="() => onProfileSuccess(step.key)"
          @error="() => onProfileError()"
        />
      </template>
      <template #step-2="{ step }">
        <Card>
          <div class="mb-8">
            <h1 class="font-comfortaa font-bold text-xl">
              {{ t("onboarding.configure.title") }}
            </h1>
            <p class="font-montserrat mt-1">
              <i18n-t
                keypath="onboarding.configure.setup_description"
                scope="global"
              >
                <template #dashboard>
                  <AppLink
                    to="/event/dashboard"
                    class="text-link hover:underline focus:underline"
                  >
                    {{ t("onboarding.configure.dashboard") }}
                  </AppLink>
                </template>
              </i18n-t>
            </p>
          </div>
          <EventDetailsForm
            :event-vault-id="eventVaultStore.eventVaultId"
            :package-subscription-id="urlParams.packageSubscriptionId"
            :show-video-field="paymentInfo?.defaultSettings?.videoEnabled"
            :event-vault-type="paymentInfo?.eventVaultType"
            @success="(data) => onEventDetailsSuccess(data, step.key)"
            @error="onEventDetailsError"
          />
        </Card>
      </template>
      <template #step-3="{ step }">
        <Card>
          <EventSettingsForm
            :save-on-change="false"
            @success="() => onEventSettingsSuccess(step.key)"
            @error="() => onEventSettingsError()"
          />
        </Card>
      </template>
    </Stepper>

    <FreemiumUpgradePromptModal
      v-if="upgradeableFreemiumEvent"
      :event-name="upgradeableFreemiumEvent.title"
      :is-loading="isUpgradingFreemiumEvent"
      @confirm="onUpgradeFreemiumEvent"
      @cancel="onCreateNewEvent"
    />
  </HostLayout>
</template>
